import type {
  DailyCheckinBoard,
  DailyCheckinClaimResult,
  DailyCheckinReward,
  DailyCheckinRewardStatus,
} from '~/types/daily-checkin';
import { resolveCloudflareImage } from '~/utils/apostatudo/cloudflare-image';

// O check-in responde em snake_case e PLANO (`period_type`/`starts_on`/
// `ends_on` na raiz). Estes mappers traduzem a caixa, normalizam (coerção de
// tipo e defaults pra campo nulo) e dobram as três chaves do período no objeto
// que o front consulta pra saber se há jogo no ar.
//
// O contrato nasceu em camelCase e virou snake_case na revisão do ENG-425 — não
// sobrou nenhum campo camelCase na API. Ler só o nome novo é uma aposta
// consciente: o front tem que subir DEPOIS do core, senão a trilha inteira mapeia
// pra vazio e o modal mostra "sem período" com o jogo no ar.

const STATUSES: DailyCheckinRewardStatus[] = ['claimed', 'open', 'missed'];

// Status desconhecido cai em 'open': é o estado neutro: sem check, sem X e sem
// virar botão (só o primeiro da fila vira). Um valor novo no core degrada pra
// "degrau que ainda vem" em vez de sumir da trilha.
function mapStatus(value: unknown): DailyCheckinRewardStatus {
  return STATUSES.includes(value as DailyCheckinRewardStatus)
    ? (value as DailyCheckinRewardStatus)
    : 'open';
}

// Destino do botão USAR (`cta_url`). Devolve um PEDAÇO de objeto pra ser
// espalhado no resultado, e não um valor: é o que preserva o tri-estado do
// contrato (ver DailyCheckinReward.ctaUrl). Chave ausente no JSON → chave
// ausente no domínio, e quem decide o fallback é a tela; chave presente com
// `null` → string vazia, que é o core dizendo "este prêmio não tem destino".
//
// Escrever `ctaUrl: undefined` aqui pareceria igual e não é: no resgate, a
// store aplica o resultado por cima do degrau do board com `Object.assign`, e
// uma chave presente valendo `undefined` sobrescreve o que o board trouxe.
function mapCtaUrl(json: any): { ctaUrl?: string } {
  if (!json || !('cta_url' in json)) return {};

  return {
    ctaUrl: typeof json.cta_url === 'string' ? json.cta_url.trim() : '',
  };
}

// A arte do prêmio vem do Cloudflare Images, que exige sufixo de variant — sem
// ele a URL 404. O core manda a URL crua, então quem completa é o adapter, no
// mesmo lugar (e com a mesma função) que os mappers da roletinha: a variant é
// detalhe de transporte, não pode vazar pro componente. `resolveCloudflareImage`
// tira a variant que porventura já tenha vindo antes de aplicar `/public`, então
// serve tanto pra URL crua quanto pra uma que já chegue pronta.
export function mapReward(json: any): DailyCheckinReward {
  return {
    position: Number(json.position),
    title: json.title ?? '',
    cardLabel: json.card_label ?? null,
    description: json.description ?? null,
    imageUrl: resolveCloudflareImage(json.image_url),
    ...mapCtaUrl(json),
    isFeatured: !!json.is_featured,
    status: mapStatus(json.status),
  };
}

export function mapBoard(json: any): DailyCheckinBoard {
  return {
    // Sem período publicado o core devolve 200 com `period_type: null` e lista
    // vazia — é o sinal pro front esconder o jogo, não um erro.
    period: json.period_type
      ? {
          type: json.period_type,
          startsOn: json.starts_on,
          endsOn: json.ends_on,
          // `terms` também vem na raiz e entra no período pelo mesmo motivo que
          // as datas: é o regulamento DAQUELE ciclo. Sem período publicado o
          // campo não tem onde existir, e o modal cai na copy de fallback.
          terms: json.terms ?? null,
        }
      : null,
    canClaimToday: !!json.can_claim_today,
    nextClaimAvailableAt: json.next_claim_available_at ?? null,
    claimedCount: Number(json.claimed_count ?? 0),
    remainingCount: Number(json.remaining_count ?? 0),
    missedDays: Number(json.missed_days ?? 0),
    rewards: Array.isArray(json.rewards) ? json.rewards.map(mapReward) : [],
  };
}

// O resgate devolve o prêmio solto na raiz, sem status nem contadores — e sem
// `card_label`, que só existe no board.
export function mapClaimResult(json: any): DailyCheckinClaimResult {
  return {
    position: Number(json.position),
    title: json.title ?? '',
    description: json.description ?? null,
    imageUrl: resolveCloudflareImage(json.image_url),
    ...mapCtaUrl(json),
    isFeatured: !!json.is_featured,
  };
}
